import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { Time } from '@shared/constants';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { SVG_ASSET_READER } from './svg-asset-reader.token';

@Injectable({ providedIn: 'root' })
export class SvgService extends ApiServiceBase<void> {
  // Memory cache for already fetched SVG content
  private readonly cache = new Map<string, string>();

  // Track ongoing requests to avoid duplicate network calls
  private readonly ongoingRequests = new Map<string, Observable<string>>();

  // SSR-only filesystem reader for local assets (avoids an HTTP self-request).
  // Absent in the browser, where the regular HTTP path is used.
  private readonly assetReader = inject(SVG_ASSET_READER, { optional: true });

  constructor() {
    super('');
  }

  /**
   * Fetch an SVG as Observable (optimized with cache + shared observable).
   * @param src URL to the SVG asset
   * @returns Observable<string> of SVG content
   */
  get(src: string): Observable<string> {
    // If it's already cached, return it immediately
    if (this.cache.has(src)) {
      return of(this.cache.get(src)!);
    }

    // On the server, read local assets straight from disk to avoid an HTTP
    // self-request to the app's own URL (deadlocks behind the Cloud Run LB).
    // Returns null for CDN sources or a missing file, falling back to HTTP.
    const localSvg = this.assetReader?.(src);
    if (localSvg != null) {
      this.cache.set(src, localSvg);
      return of(localSvg);
    }

    // If a request is already ongoing, return that observable
    if (this.ongoingRequests.has(src)) {
      return this.ongoingRequests.get(src)!;
    }

    // Otherwise, make the request and store it temporarily
    const request$ = this.customRequest<string>({
      method: 'GET',
      externalUrl: src,
      options: {
        responseType: 'text',
        headers: new HttpHeaders({
          Accept: 'image/svg+xml',
        }),
      },
      cache: {
        cacheExpirationTime: Time.TEN_HOURS,
        store: 'ASSETS',
      },
    }).pipe(
      tap((svg) => {
        this.cache.set(src, svg);
        this.ongoingRequests.delete(src);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.ongoingRequests.set(src, request$);
    return request$;
  }
}
